import type { OperationsResponse } from '../lib/api';
import { Card } from '../components/Painel';

/**
 * Operacoes e integracoes (Back Office SPEC §7.4 e Fase 2 da §23).
 *
 * O que EXISTE de verdade na Fase 0: estado do sistema, pausas, e o registro de
 * capacidade externa. E o registro de capacidade e a tela mais importante desta
 * fase, porque `unknown` significa "nao chamar" — e o operador precisa ver que
 * nada foi validado ainda.
 */

const CAPABILITY_STATE_LABEL: Record<string, string> = {
  available: 'validada e disponível',
  unavailable: 'validada e INDISPONÍVEL',
  unknown: 'não validada',
  stale: 'validação vencida',
};

/** Explicacao em linguagem simples de cada estado. */
const CAPABILITY_STATE_MEANING: Record<string, string> = {
  available: 'O sistema pode usar este recurso.',
  unavailable: 'Confirmado que o provedor não oferece; o fallback vale.',
  unknown: 'Nunca conferida contra a documentação oficial. Trata como indisponível.',
  stale: 'Foi validada, mas há mais de 90 dias. Não vale como evidência.',
};

/**
 * Os oito estados do Console SPEC §139, com O QUE FAZER em cada um.
 *
 * A ação é a razão de os oito existirem: pedir permissão resolve
 * `PERMISSION_MISSING` e não resolve `ACCOUNT_RESTRICTED`; esperar resolve
 * `TEMPORARILY_UNAVAILABLE` e não resolve `DEPRECATED`. Um rótulo sem ação
 * deixaria o operador com o mesmo "indisponível" de antes.
 */
const CAPABILITY_STATUS_LABEL: Record<string, string> = {
  SUPPORTED: 'Suportada',
  UNSUPPORTED: 'Não suportada',
  NOT_TESTED: 'Nunca verificada',
  ACCOUNT_RESTRICTED: 'Conta restrita',
  PERMISSION_MISSING: 'Falta permissão',
  DEPRECATED: 'Descontinuada',
  TEMPORARILY_UNAVAILABLE: 'Indisponível temporariamente',
  UNKNOWN: 'Sem registro',
};

const CAPABILITY_STATUS_ACAO: Record<string, string> = {
  SUPPORTED: '',
  UNSUPPORTED: 'Engenharia: usar o caminho alternativo. Não há o que pedir à plataforma.',
  NOT_TESTED: 'Operação: verificar na documentação oficial e registrar o método.',
  ACCOUNT_RESTRICTED: 'Operação: a restrição é da conta. Falar com a plataforma, não pedir escopo.',
  PERMISSION_MISSING: 'Operação: pedir o escopo que falta e revalidar.',
  DEPRECATED: 'Produto: escolher o substituto. Não volta.',
  TEMPORARILY_UNAVAILABLE: 'Nada a fazer agora: volta sozinha. Revalidar depois.',
  UNKNOWN: 'Engenharia: a capacidade não está no registro.',
};

export function Operations({ operations }: { operations: OperationsResponse }) {
  const byProvider = new Map<string, OperationsResponse['capabilities']>();
  for (const c of operations.capabilities) {
    const list = byProvider.get(c.provider) ?? [];
    list.push(c);
    byProvider.set(c.provider, list);
  }

  const notValidated = operations.capabilities.filter((c) => c.state === 'unknown').length;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Monitoramento e logs</h1>

      <section aria-labelledby="secao-capacidades">
        <Card>
          <h2
            id="secao-capacidades"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            Registro de capacidade externa
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-ink-muted">
            Capacidade de API externa é bandeira <strong>validada</strong>, não suposição
            permanente. Capacidade não validada conta como indisponível: o sistema não faz a
            chamada.
          </p>
          {notValidated > 0 && (
            <p className="mt-2 rounded border border-line-strong bg-surface px-3 py-2 text-sm">
              <strong>{notValidated}</strong> de <strong>{operations.capabilities.length}</strong>{' '}
              capacidades ainda não foram validadas contra a documentação oficial. Enquanto isso,
              todo caminho que depende delas falha fechado.
            </p>
          )}

          {[...byProvider.entries()].map(([provider, caps]) => (
            <div key={provider} className="mt-4">
              <h3 className="text-sm font-semibold">{provider}</h3>
              <div className="mt-1 overflow-x-auto">
                <table className="w-full min-w-[32rem] border-collapse text-sm">
                  <caption className="sr-only">{`Capacidades do provedor ${provider}`}</caption>
                  <thead>
                    <tr className="border-b border-line text-left">
                      <th scope="col" className="py-1 pr-3 font-semibold">
                        Capacidade
                      </th>
                      <th scope="col" className="py-1 pr-3 font-semibold">
                        Estado
                      </th>
                      <th scope="col" className="py-1 pr-3 font-semibold">
                        Diagnóstico
                      </th>
                      <th scope="col" className="py-1 pr-3 font-semibold">
                        Validada em
                      </th>
                      <th scope="col" className="py-1 font-semibold">
                        Fallback
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {caps.map((c) => (
                      <tr key={c.capability} className="border-b border-line align-top">
                        <th scope="row" className="py-1 pr-3 font-normal">
                          {c.capability}
                        </th>
                        <td className="py-1 pr-3">
                          <span
                            className={
                              c.state === 'available' ? 'font-semibold' : 'italic text-ink-muted'
                            }
                            data-state={c.state}
                          >
                            {CAPABILITY_STATE_LABEL[c.state] ?? c.state}
                          </span>
                          <span className="sr-only">
                            {` ${CAPABILITY_STATE_MEANING[c.state] ?? ''}`}
                          </span>
                        </td>
                        <td className="py-1 pr-3">
                          <span data-status={c.status}>
                            {CAPABILITY_STATUS_LABEL[c.status] ?? c.status}
                          </span>
                          {CAPABILITY_STATUS_ACAO[c.status] ? (
                            <span className="block text-xs text-ink-muted">
                              {CAPABILITY_STATUS_ACAO[c.status]}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1 pr-3 text-ink-muted">
                          {c.validated_at ?? <span className="italic">nunca</span>}
                        </td>
                        <td className="py-1 text-ink-muted">{c.fallback}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </Card>
      </section>

      <section aria-labelledby="secao-projecoes">
        <Card>
          <h2
            id="secao-projecoes"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            Projeções e frescor
          </h2>
          {operations.projections_state === 'not_configured' ? (
            <p className="mt-2 rounded border border-dashed border-line-strong px-3 py-2 text-sm text-ink-muted">
              Nenhuma projeção registrada. Projeção atrasada muda o status de frescor e não pode ser
              escondida, e por isso a ausência aparece como estado, não como lista vazia.
            </p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[28rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line text-left">
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Projeção
                    </th>
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Versão
                    </th>
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Frescor
                    </th>
                    <th scope="col" className="py-1 font-semibold">
                      Último evento
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {operations.projections.map((p) => (
                    <tr key={p.name} className="border-b border-line">
                      <th scope="row" className="py-1 pr-3 font-normal">
                        {p.name}
                      </th>
                      <td className="py-1 pr-3">{p.version}</td>
                      <td className="py-1 pr-3">{p.freshness}</td>
                      <td className="py-1 text-ink-muted">
                        {p.last_processed_at ?? <span className="italic">nunca</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
